"use client";

import { useMemo } from "react";
import { formatINR, formatNumber } from "@/lib/calculators/rates";

interface DonutChartProps {
  segments: Array<{
    label: string;
    value: number;
    color: string;
  }>;
  total?: number;
  isCurrency?: boolean;
}

/**
 * SVG donut/ring chart component.
 * Displays segments with legend showing values.
 */
export function DonutChart({ segments, total, isCurrency = true }: DonutChartProps) {
  const chartData = useMemo(() => {
    const sum = segments.reduce((acc, seg) => acc + seg.value, 0);
    const totalValue = total || sum;

    // Calculate paths for donut
    let currentAngle = -Math.PI / 2; // Start from top
    const radius = 45;
    const innerRadius = 25;

    const paths = segments.map((seg) => {
      const sliceAngle = (seg.value / totalValue) * 2 * Math.PI;
      const startAngle = currentAngle;
      const endAngle = currentAngle + sliceAngle;

      // Calculate points
      const x1 = 50 + radius * Math.cos(startAngle);
      const y1 = 50 + radius * Math.sin(startAngle);
      const x2 = 50 + radius * Math.cos(endAngle);
      const y2 = 50 + radius * Math.sin(endAngle);

      const ix1 = 50 + innerRadius * Math.cos(startAngle);
      const iy1 = 50 + innerRadius * Math.sin(startAngle);
      const ix2 = 50 + innerRadius * Math.cos(endAngle);
      const iy2 = 50 + innerRadius * Math.sin(endAngle);

      const largeArc = sliceAngle > Math.PI ? 1 : 0;

      const path = [
        `M ${ix1} ${iy1}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
        `L ${ix2} ${iy2}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1}`,
        "Z",
      ].join(" ");

      currentAngle = endAngle;

      return {
        path,
        color: seg.color,
        label: seg.label,
        value: seg.value,
        percentage: ((seg.value / totalValue) * 100).toFixed(1),
      };
    });

    return { paths, total: totalValue };
  }, [segments, total]);

  const centerTotal = isCurrency
    ? formatINR(chartData.total)
    : formatNumber(chartData.total);

  return (
    <div className="space-y-4">
      {/* Donut Chart */}
      <div className="flex justify-center">
        <svg viewBox="0 0 100 100" width={200} height={200} className="drop-shadow-sm">
          {chartData.paths.map((path, idx) => (
            <path key={idx} d={path.path} fill={path.color} stroke="white" strokeWidth="0.5" />
          ))}

          {/* Center text */}
          <circle cx="50" cy="50" r="25" fill="url(#centerGradient)" />
          <defs>
            <radialGradient id="centerGradient">
              <stop offset="0%" stopColor="rgba(62, 207, 142, 0.05)" />
              <stop offset="100%" stopColor="rgba(62, 207, 142, 0)" />
            </radialGradient>
          </defs>
          <text
            x="50"
            y="50"
            textAnchor="middle"
            dy="0.3em"
            className="text-[8px] font-bold fill-foreground"
          >
            {centerTotal}
          </text>
        </svg>
      </div>

      {/* Legend */}
      <div className="space-y-2">
        {chartData.paths.map((path, idx) => (
          <div key={idx} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: path.color }}
              />
              <span className="text-foreground/90">{path.label}</span>
            </div>
            <div className="text-right">
              <div className="font-semibold">
                {isCurrency ? formatINR(path.value) : formatNumber(path.value)}
              </div>
              <div className="text-xs text-muted-foreground">{path.percentage}%</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
